import {toast} from "sonner";

import {captureGpsFailure} from "@/src/modules/core/lib/glitchtip/device-capture";
import {formatGpsCoordinates} from "@/src/modules/core/lib/format-gps-coordinates";
import {
  geolocationService,
  isGeolocationPermissionDenied,
} from "@/src/modules/core/lib/geolocation-service";
import type {ResolvedGpsPayload} from "@/src/modules/core/types/geolocation";

import type {ResolveGpsStringOptions} from "./resolve-gps-coordinates-string";

const LOCATION_CAPTURE_FAILURE =
  "Couldn't capture your location. Turn on GPS / location and make sure you have signal, then try again.";

/**
 * Device-only GPS for patrol checkpoint checks.
 * No cache / property / manual fallback — backend hard-rejects missing coords for every role.
 */
export async function resolveDeviceGpsForSubmit(
  config: ResolveGpsStringOptions = {onFailure: "toast"},
): Promise<ResolvedGpsPayload | null> {
  if (isGeolocationPermissionDenied()) {
    if (config.onFailure === "toast") {
      toast.error(LOCATION_CAPTURE_FAILURE);
    }
    if (config.captureContext) {
      captureGpsFailure(config.captureContext);
    }
    return null;
  }

  const buffered = geolocationService.getLastFix();
  if (buffered?.source === "device" && geolocationService.isWatchBufferFresh(buffered)) {
    const formatted = formatGpsCoordinates(buffered);
    if (formatted) {
      return {gps_coordinates: formatted, gps_source: "device"};
    }
  }

  await geolocationService.awaitInFlightAcquire();

  const bufferedAfterPrewarm = geolocationService.getLastFix();
  if (
    bufferedAfterPrewarm?.source === "device" &&
    geolocationService.isWatchBufferFresh(bufferedAfterPrewarm)
  ) {
    const formatted = formatGpsCoordinates(bufferedAfterPrewarm);
    if (formatted) {
      return {gps_coordinates: formatted, gps_source: "device"};
    }
  }

  try {
    const fix = await geolocationService.acquirePosition({
      fresh: true,
      allowCache: false,
      allowPropertyFallback: false,
      allowCoarseRetry: false,
      propertyCoordinates: null,
    });

    if (fix.source !== "device") {
      if (config.onFailure === "toast") {
        toast.error(LOCATION_CAPTURE_FAILURE);
      }
      if (config.captureContext) {
        captureGpsFailure(config.captureContext);
      }
      return null;
    }

    const formatted = formatGpsCoordinates(fix);
    if (!formatted) {
      if (config.onFailure === "toast") {
        toast.error(LOCATION_CAPTURE_FAILURE);
      }
      if (config.captureContext) {
        captureGpsFailure(config.captureContext);
      }
      return null;
    }

    return {gps_coordinates: formatted, gps_source: "device"};
  } catch {
    if (config.onFailure === "toast") {
      toast.error(LOCATION_CAPTURE_FAILURE);
    }
    if (config.captureContext) {
      captureGpsFailure(config.captureContext);
    }
    return null;
  }
}
